interface SkeletonProps {
  variant: "card" | "table" | "text" | "stat";
  rows?: number;
  count?: number;
}

function ShimmerBar({ className = "" }: { className?: string }) {
  return (
    <div
      className={`rounded bg-slate-200 relative overflow-hidden ${className}`}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
          animation: "shimmer 1.5s ease-in-out infinite",
        }}
      />
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <ShimmerBar className="h-4 w-2/3 mb-3" />
      <ShimmerBar className="h-3 w-full mb-2" />
      <ShimmerBar className="h-3 w-4/5" />
    </div>
  );
}

function TableSkeleton({ rows = 5 }: { rows: number }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <ShimmerBar className="h-4 w-48" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-3 border-b border-slate-50 last:border-0"
        >
          <ShimmerBar className="h-3 flex-1" />
          <ShimmerBar className="h-3 w-20" />
          <ShimmerBar className="h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

function TextSkeleton() {
  return (
    <div className="space-y-2.5">
      <ShimmerBar className="h-3 w-full" />
      <ShimmerBar className="h-3 w-4/5" />
      <ShimmerBar className="h-3 w-3/5" />
    </div>
  );
}

function StatSkeleton() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <ShimmerBar className="w-10 h-10 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <ShimmerBar className="h-3 w-16" />
        <ShimmerBar className="h-5 w-24" />
      </div>
    </div>
  );
}

export function Skeleton({ variant, rows = 5, count = 1 }: SkeletonProps) {
  if (variant === "table") {
    return <TableSkeleton rows={rows} />;
  }

  if (variant === "text") {
    return <TextSkeleton />;
  }

  const items = Array.from({ length: count });
  if (variant === "stat") {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {items.map((_, i) => (
          <StatSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}
